/** @jsx jsx */
import { ReactElement } from 'react';
import { css, jsx } from '@emotion/core';
import styled from '@emotion/styled';
import { Grid, Hidden, IconButton } from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBackRounded';
import ArrowForwardIcon from '@material-ui/icons/ArrowForwardRounded';
import { useTranslation } from 'react-i18next';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';

import { ICustomTheme, WidgetsType } from 'interfaces';
import { Text } from 'components';
import { TCurrentGlobalViewWidgetSet } from 'interfaces/widgets.types';
import Widget from 'components/pages/Content/Widget';

const StyledIconButton = styled(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  ({ left, right, ...props }) => <IconButton {...props} />,
  // purposely exclude left & right props to avoid Material errors
)<{ theme: ICustomTheme; left?: boolean; right?: boolean }>(
  ({ theme, left = false, right = false }) => ({
    backgroundColor: theme.colors.black.dark3,
    borderRadius: theme.shape.borderRadius * 10,
    color: theme.colors.white,
    left: left ? theme.spacing(8) : 'unset',
    padding: theme.spacing(1),
    position: 'absolute',
    right: right ? theme.spacing(8) : 'unset',
    top: `calc(50% - (1.5rem + ${theme.spacing(1)}px))`,
    // 50% - halfTheButtonHeight to be sure it's vertically centered
    transition: theme.transitions.create(['background-color', 'color']),
    zIndex: 1,
    opacity: 0,
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.7)',
      color: theme.colors.black.dark1,
    },
  }),
);

const StyledIndicator = styled.li<{
  theme: ICustomTheme;
  isSelected: boolean;
}>(({ theme, isSelected }) => ({
  backgroundColor: theme.colors.black.dark3,
  borderRadius: '100%',
  cursor: isSelected ? 'default' : 'pointer',
  display: 'inline-block',
  height: theme.spacing(1.25),
  margin: theme.spacing(0, 0.75, 2),
  opacity: isSelected ? 1 : 0.5,
  transition: theme.transitions.create(['opacity', 'background-color']),
  width: theme.spacing(1.25),
  ...(isSelected
    ? {}
    : {
        '&:hover': {
          backgroundColor: theme.colors.black.dark1,
        },
      }),
}));

const cssContainer = (theme: ICustomTheme) => ({
  margin: theme.spacing(-1, -1.5),
  '@media (max-width: 960px)': {
    '& .control-dots': {
      zIndex: 0,
    },
  },
  '& .slide': {
    background: 'none',
    padding: theme.spacing(1, 0),
  },
  '& .slider-wrapper': {
    minHeight: '30.35vw',
    // corresponding to WidgetEdito's img min-height
    // needed or the first widget looks collapsed at first
  },
  '&:hover': {
    '.MuiButtonBase-root': {
      opacity: 1,
    },
  },
});
const cssGrouped = (theme: ICustomTheme) => ({
  padding: theme.spacing(0, 5),
});
const cssContainerGrouped = (theme: ICustomTheme) => [
  cssContainer(theme),
  cssGrouped(theme),
];
const cssLargeIcon = { fontSize: '3rem' };
const cssMessage = css({
  paddingTop: '15%',
  textAlign: 'center',
  width: '100%',
});

const WidgetsCarouselContainer = ({
  currentGlobalViewWidgetSet,
  widgets,
  dynamicHeight,
  groupedWidgets,
  isFullWidth,
  actionPhase,
}: {
  currentGlobalViewWidgetSet?: TCurrentGlobalViewWidgetSet;
  widgets?: WidgetsType[];
  dynamicHeight: boolean;
  groupedWidgets?: WidgetsType[][];
  isFullWidth: boolean;
  actionPhase?: string;
}): ReactElement => {
  const { t } = useTranslation();

  if (!widgets?.length && !groupedWidgets?.length) {
    return (
      <Text css={cssMessage}>
        <em>
          {(() => {
            switch (actionPhase) {
              case 'running':
                return t('widgets.no_widget_to_display_running');
              case 'incoming':
                return t('widgets.no_widget_to_display_incoming');
              case 'ended':
                return t('widgets.no_widget_to_display_ended');
              default:
                return t('widgets.no_widget_to_display');
            }
          })()}
        </em>
      </Text>
    );
  }

  if (widgets?.length === 1) {
    return (
      <div css={cssContainer}>
        <Widget widget={widgets[0]} isFullWidth={isFullWidth} />
      </div>
    );
  }

  if (groupedWidgets?.length === 1) {
    return (
      <Grid container justify="center" css={cssContainerGrouped}>
        {groupedWidgets[0].map((widget) => (
          <Grid item xs={4} key={widget['@id']}>
            <Widget widget={widget} third />
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <Carousel
      autoPlay
      dynamicHeight={dynamicHeight}
      stopOnHover
      infiniteLoop
      emulateTouch
      interval={3000}
      showThumbs={false}
      showStatus={false}
      css={cssContainer}
      renderArrowPrev={(onClickHandler) => (
        <Hidden only="xs">
          <StyledIconButton left onClick={onClickHandler}>
            <ArrowBackIcon css={cssLargeIcon} />
          </StyledIconButton>
        </Hidden>
      )}
      renderArrowNext={(onClickHandler) => (
        <Hidden only="xs">
          <StyledIconButton right onClick={onClickHandler}>
            <ArrowForwardIcon css={cssLargeIcon} />
          </StyledIconButton>
        </Hidden>
      )}
      renderIndicator={(onClickHandler, isSelected) => (
        <StyledIndicator
          onClick={onClickHandler}
          isSelected={isSelected}
          role="button"
        />
      )}
    >
      {widgets?.length
        ? widgets.map((widget) => (
            <Widget
              key={widget['@id']}
              widget={widget}
              isFullWidth={isFullWidth}
              noMarginAdjust
              currentGlobalViewWidgetSet={currentGlobalViewWidgetSet}
            />
          ))
        : groupedWidgets?.map((groupedWidget) => (
            <Grid container justify="center" css={cssGrouped}>
              {groupedWidget.map((widget) => (
                <Grid item xs={4} key={widget['@id']}>
                  <Widget
                    widget={widget}
                    third
                    currentGlobalViewWidgetSet={currentGlobalViewWidgetSet}
                  />
                </Grid>
              ))}
            </Grid>
          ))}
    </Carousel>
  );
};

WidgetsCarouselContainer.defaultProps = {
  dynamicHeight: true,
  isFullWidth: false,
};

export default WidgetsCarouselContainer;
